import classnames from 'classnames';
import { useProductCardContext } from 'palette/ProductCard';
import { CARD_THEMES } from 'palette/ProductCard/constants';
import { CardTheme } from 'palette/ProductCard/types/cardElements';
import { IPriceObject } from 'palette/ProductCard/types/productData';
import ProductSplash from 'palette/ProductSplashes/ProductSplash';
import { BodyText, Color } from 'palette/Typography';

import './Price.scss';

interface PricesProps {
    prices: IPriceObject;
    bemModifier?: string;
    floatPriceToRight?: boolean;
}

const getColor = ({
    isPreviousPrice,
    isPriceOnDiscount,
    hasToStrikeTrough,
    theme,
}: {
    isPreviousPrice?: boolean;
    isPriceOnDiscount?: boolean;
    hasToStrikeTrough?: boolean;
    theme?: CardTheme;
}): Color => {
    const isDarkTheme = theme === CARD_THEMES.dark;
    if (hasToStrikeTrough) { return isDarkTheme ? 'lighter' : 'light'; }
    if (isPreviousPrice) { return isDarkTheme ? 'white' : 'regular'; }
    if (isPriceOnDiscount) { return 'attention'; }
    return isDarkTheme ? 'white' : 'strong';
};

function Prices({
    prices,
    bemModifier,
    floatPriceToRight,
}: PricesProps) {
    const { orientation, theme } = useProductCardContext();
    const priceContainerClasses = classnames('palette-product-card-price', {
        'palette-product-card-price--float-right': floatPriceToRight,
        [`palette-product-card-price--${bemModifier}`]: bemModifier,
    });
    const { splash, values = [] } = prices || {};

    const isFullWidth = (index: number, isLongPrice: boolean) => {
        if (floatPriceToRight || isLongPrice) {
            return true;
        }

        if (orientation === 'vertical') {
            const hasLongPrice = values.find(price => price.isLongPrice);
            return index === 0 && (values.length === (hasLongPrice ? 4 : 3));
        }

        return false;
    };

    return values.length ? (
        <div className="palette-product-card-price">
            <div className={priceContainerClasses}>
                {
                    values.map((
                        {
                            isPreviousPrice,
                            value,
                            isPriceOnDiscount,
                            isLongPrice,
                            hasToStrikeTrough,
                        },
                        index,
                    ) => (
                        <BodyText
                            key={value}
                            className={classnames('palette-product-card-price__price-tag', {
                                'palette-product-card-price__price-tag--full-width': isFullWidth(index, isLongPrice),
                            })}
                            color={getColor({
                                isPreviousPrice, isPriceOnDiscount, hasToStrikeTrough, theme,
                            })}
                            isStrikeTrough={hasToStrikeTrough}
                            weight={isPreviousPrice ? 'regular' : 'bold'}
                        >
                            {value}
                        </BodyText>
                    ))
                }

            </div>
            {
                !!splash?.label
                && (
                    <ProductSplash
                        backgroundColor={splash.backgroundColor}
                        color={splash.color}
                        label={splash.label}
                    />
                )
            }
        </div>
    ) : null;
}

export default Prices;
